import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useMembersContext } from '@/contexts/membersContext';
import FieldError from '@/forms/FieldError';
import useTranslate from '@/hooks/useTranslate';
import AuthRepository from '@/repositories/members/AuthRepository';
import Button from '@/ui/members/components/Button';
import InputField from '@/ui/members/components/form/InputField';
import InputGroup from '@/ui/members/components/form/InputGroup';
import isRunningInReactNativeWebView from '@/utils/isRunningInReactNativeWebView';

import MobileCloseButton from '../../components/MobileCloseButton';

interface LoginFormValues {
  username: string;
  password: string;
}

const Login = () => {
  const authRepository = AuthRepository();
  const membersContext = useMembersContext();
  const navigate = useNavigate();
  const { t } = useTranslate('pages.members.pages.Login');

  const login = useMutation({
    mutationFn: (values: LoginFormValues) => {
      return authRepository.login(values.username, values.password);
    },
    onSuccess: (data) => {
      membersContext.setMemberData(data);
      navigate('/', { replace: true });
    },
    onError: (error) => {
      console.error('Login failed:', error);
    }
  });

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormValues>({
    defaultValues: {
      username: '',
      password: ''
    }
  });

  const loginForm = (values: LoginFormValues) => {
    const { username, password } = values;

    login.mutate({ username, password });
  };

  return (
    <div className="flex items-center justify-cente flex-col">
      {isRunningInReactNativeWebView() && <MobileCloseButton />}
      <form onSubmit={handleSubmit(loginForm)} className="flex flex-col gap-4 mt-10 text-black">
        <InputGroup>
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <InputField
                label="Username"
                id="username"
                type="text"
                placeholder={t('enterYourUsername')}
                {...field}
              />
            )}
            rules={{ required: t('usernameIsRequired') }}
          />
          <FieldError message={errors.username?.message} />
        </InputGroup>
        <InputGroup>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <InputField
                label="Password"
                id="password"
                type="password"
                placeholder={t('enterYourPassword')}
                {...field}
              />
            )}
            rules={{ required: t('passwordIsRequired') }}
          />
          <FieldError message={errors.password?.message} />
        </InputGroup>
        <InputGroup>
          <Button type="submit" disabled={login.isPending} className="w-full">
            Submit
          </Button>
        </InputGroup>
      </form>
    </div>
  );
};

export default Login;
